import { action, computed, makeObservable, observable } from "mobx";
import { DefaultRequestStatus, RequestStatus } from "../services/services/requestStatus";
import { DefaultProposalResponse, ProposalResponseModel } from "../helpers/proposalResponse";
import { PaymentMethodTypes } from "../helpers/paymentMethod";
import { NnwProposal, NnwProposalModel } from "../helpers/nnwProposalModel";
import ConsentsValues, {
  AdditionalConsentsId,
  ConsentsModel,
  RequiredConsentsId
} from "../helpers/consents";
import { ValidatedValue } from "../helpers/validator";
import { AnimalsProposal, AnimalsProposalModel } from "../helpers/catsAndDogsProposalModel";
import { GravestoneProposal, GravestoneProposalModel } from "../helpers/gravestoneProposalModel";
import { SafeSeniorProposal, SafeSeniorProposalModel } from "../helpers/safeSeniorProposalModel";
import { CessionBankModel, DefaultCessionBank } from "../helpers/cessionBank";
import { issuePlaceValidators } from "../helpers/issuePlace";
import { SeniorProposalModel } from "../helpers/seniorProposalModel";
import { distancePolicyValidators } from "../helpers/distancePolicyValidator";
import { ClientContactTypes } from "../helpers/clientContact";
import { PreviousInsurerNameTypes } from "../helpers/previousInsurerName";
import { CalculateStore } from "./calculateStore";
import { isClientTuzMember } from "../helpers/client";

export interface ProposalStore {
  // Requests
  proposalRequestStatus: RequestStatus;

  // ProposalForm
  paymentMethod: PaymentMethodTypes;
  setPaymentMethod: (paymentMethod: PaymentMethodTypes) => void;
  paymentToDate: Date | null;
  setPaymentToDate: (date: Date) => void;
  resetPaymentToDate: () => void;
  issuePlace: ValidatedValue<string>;
  setIssuePlace: (issuePlace: string) => void;
  nnw: NnwProposalModel;
  catAndDog: AnimalsProposalModel;
  gravestone: GravestoneProposalModel;
  safeSenior: SafeSeniorProposalModel;
  setSafeSenior: (safeSenior: SeniorProposalModel[]) => void;

  cession: CessionBankModel;
  cessionBanks: string[];
  setCessionBanks: (value: string[]) => void;

  consents: ConsentsModel;
  setConsent: (id: RequiredConsentsId | AdditionalConsentsId, checked: boolean) => void;
  setRequireConsents: (value: boolean, calculateStore: CalculateStore, isDirect: boolean) => void;
  allRequiredConsentsCheckbox: boolean;
  setAllRequiredConsentsCheckbox: (value: boolean) => void;
  checkIfAllRequiredConsentsChecked: (calculateStore: CalculateStore, isDirect: boolean) => boolean;
  validateRequiredConsents: (calculateStore: CalculateStore, isDirect: boolean) => void;
  distancePolicy: ValidatedValue<boolean | undefined>;

  // Eurofinance
  authorisedOfwca?: string;
  setAuthorisedOfwca: (authorisedOfwca: string | undefined) => void;

  clientContact: ClientContactTypes;
  setClientContact: (clientContact: ClientContactTypes) => void;

  previousInsurerName: PreviousInsurerNameTypes;
  setPreviousInsurerName: (previousInsurerName: PreviousInsurerNameTypes) => void;

  // Other
  proposalResponse: ProposalResponseModel;
  setProposalResponse: (proposalResponse: ProposalResponseModel) => void;

  resetStore: () => void;
}

export class DefaultProposalStore implements ProposalStore {
  constructor() {
    makeObservable(this);
  }

  // ProposalForm
  @observable
  private _paymentMethod: PaymentMethodTypes = PaymentMethodTypes.TRANSFER;

  @computed
  public get paymentMethod(): PaymentMethodTypes {
    return this._paymentMethod;
  }

  @action
  public setPaymentMethod(paymentMethod: PaymentMethodTypes) {
    this._paymentMethod = paymentMethod;
  }

  @observable
  private _paymentToDate: Date | null = null;

  @computed
  public get paymentToDate() {
    return this._paymentToDate;
  }

  @action
  public setPaymentToDate(paymentToDate: Date) {
    paymentToDate.setHours(2,0,0,0);
    this._paymentToDate = paymentToDate;
  }

  @action
  public resetPaymentToDate() {
    this._paymentToDate = null;
  }

  @observable
  private _issuePlace: ValidatedValue<string> = new ValidatedValue("", issuePlaceValidators.issuePlace);

  @computed
  public get issuePlace() {
    return this._issuePlace;
  }

  @action
  public setIssuePlace(issuePlace: string) {
    this._issuePlace.value = issuePlace;
  }

  @observable
  private _nnw: NnwProposalModel = new NnwProposal();

  @computed
  public get nnw(): NnwProposalModel {
    return this._nnw;
  }

  @observable
  private _catAndDog: AnimalsProposalModel = new AnimalsProposal();

  @computed
  public get catAndDog(): AnimalsProposalModel {
    return this._catAndDog;
  }

  @observable
  private _gravestone: GravestoneProposalModel = new GravestoneProposal();

  @computed
  public get gravestone(): GravestoneProposalModel {
    return this._gravestone;
  }

  @observable
  private _cessionBanks: string[] = [];

  @computed
  public get cessionBanks(): string[] {
    return this._cessionBanks;
  }

  @action
  public setCessionBanks(value: string[]) {
    this._cessionBanks = value;
  }

  @observable
  private _cession: CessionBankModel = new DefaultCessionBank();

  @computed
  public get cession(): CessionBankModel {
    return this._cession;
  }

  @observable
  private _safeSenior: SafeSeniorProposalModel = new SafeSeniorProposal();

  @computed
  public get safeSenior(): SafeSeniorProposalModel {
    return this._safeSenior;
  }

  @action
  public setSafeSenior(safeSeniors: SeniorProposalModel[]) {
    this._safeSenior = new SafeSeniorProposal({ seniors: safeSeniors });
  }

  @observable
  private _consents: ConsentsValues = new ConsentsValues();

  @computed
  public get consents(): ConsentsValues {
    return this._consents;
  }

  @action
  public setConsent(id: RequiredConsentsId | AdditionalConsentsId, checked: boolean) {
    this._consents[id].value = checked;

    if (RequiredConsentsId.hasOwnProperty(id)) {
      if (this._consents[id].hasOwnProperty("error")) {
        // @ts-ignore
        this._consents[id].error = !checked;

      }
    }
  }

  @observable
  private _allRequiredConsentsCheckbox: boolean = false;

  @computed
  public get allRequiredConsentsCheckbox(): boolean {
    return this._allRequiredConsentsCheckbox;
  }

  @action
  public setAllRequiredConsentsCheckbox(value: boolean) {
    this._allRequiredConsentsCheckbox = value;
  }

  public checkIfAllRequiredConsentsChecked(calculateStore: CalculateStore, isDirect: boolean): boolean {
    const consents = this._consents;

    return (isDirect ? consents.termsOfService.value : true) &&
      consents.owu.value &&
      consents.salary.value &&
      consents.insured.value &&
      consents.productCard.value &&
      (isClientTuzMember(calculateStore) ? true : consents.accessionToTuz.value) &&
      (calculateStore.additionalInsurances.nnwType.checked ? consents.nnw.value : true);
  }
  @action
  public setRequireConsents(value: boolean, calculateStore: CalculateStore, isDirect: boolean) {
    this._consents = {
      ...this._consents,
      owu: { value, error: !value },
      salary: { value, error: !value },
      insured: { value, error: !value },
      productCard: { value, error: !value }
    };

    if (isDirect) {
      this._consents.termsOfService = { value, error: !value };
    } else {
      this._consents.termsOfService = { value: false, error: false };
    }

    if (isClientTuzMember(calculateStore)) {
      this._consents.accessionToTuz = { value: false, error: false };
    } else {
      this._consents.accessionToTuz = { value, error: !value };
    }

    if (calculateStore.additionalInsurances.nnwType.checked) {
      this._consents.nnw = { value, error: !value };
    } else {
      this._consents.nnw = { value: false, error: false };
    }
  }

  @action
  public validateRequiredConsents(calculateStore: CalculateStore, isDirect: boolean) {
    const consents = this._consents;

    consents.termsOfService.error = isDirect ? !consents.termsOfService.value : false;
    consents.owu.error = !consents.owu.value;
    consents.salary.error = !consents.salary.value;
    consents.insured.error = !consents.insured.value;
    consents.productCard.error = !consents.productCard.value;
    consents.accessionToTuz.error = isClientTuzMember(calculateStore) ? false : !consents.accessionToTuz.value;
    consents.nnw.error = calculateStore.additionalInsurances.nnwType.checked ? !consents.nnw.value : false;
  }

  @action
  public resetConsents() {
    this._consents = new ConsentsValues();
  }

  @observable
  private _distancePolicy: ValidatedValue<boolean | undefined> = new ValidatedValue(undefined, distancePolicyValidators.distancePolicy);

  @computed
  public get distancePolicy() {
    return this._distancePolicy;
  }

  // Eurofinance
  @observable
  private _authorisedOfwca?: string;

  @computed
  public get authorisedOfwca() {
    return this._authorisedOfwca;
  }

  @action
  public setAuthorisedOfwca(authorisedOfwca: string | undefined) {
    this._authorisedOfwca = authorisedOfwca;
  }

  @observable
  private _clientContact: ClientContactTypes = ClientContactTypes.COMPANY;

  @computed
  public get clientContact() {
    return this._clientContact;
  }

  @action
  public setClientContact(clientContact: ClientContactTypes) {
    this._clientContact = clientContact;
  }

  @observable
  private _previousInsurerName: PreviousInsurerNameTypes = PreviousInsurerNameTypes.NOT_APPLICABLE;

  @computed
  public get previousInsurerName() {
    return this._previousInsurerName;
  }

  @action
  public setPreviousInsurerName(previousInsurerName: PreviousInsurerNameTypes) {
    this._previousInsurerName = previousInsurerName;
  }

  // Requests
  @observable
  private _proposalRequestStatus: RequestStatus = new DefaultRequestStatus();

  @computed
  public get proposalRequestStatus(): RequestStatus {
    return this._proposalRequestStatus;
  }

  @observable
  private _cessionBanksRequestStatus: RequestStatus = new DefaultRequestStatus();

  @computed
  public get cessionBanksRequestStatus() {
    return this._cessionBanksRequestStatus;
  }

  @observable
  private _proposalResponse: ProposalResponseModel = DefaultProposalResponse;

  @computed
  public get proposalResponse() {
    return this._proposalResponse;
  }

  @action
  public setProposalResponse(proposalResponse: ProposalResponseModel) {
    this._proposalResponse = proposalResponse;
  }

  @action
  public resetStore() {
    this._distancePolicy = new ValidatedValue(undefined, distancePolicyValidators.distancePolicy);
    this._paymentMethod = PaymentMethodTypes.TRANSFER;
    this._proposalRequestStatus = new DefaultRequestStatus();
    this._proposalResponse = DefaultProposalResponse;
    this._catAndDog = new AnimalsProposal();
    this._nnw = new NnwProposal();
    this._gravestone = new GravestoneProposal();
    this._safeSenior = new SafeSeniorProposal();
    this._cession = new DefaultCessionBank();
    this._issuePlace = new ValidatedValue("", issuePlaceValidators.issuePlace);
    this.resetConsents();
    this._authorisedOfwca = "";
    this._previousInsurerName = PreviousInsurerNameTypes.NOT_APPLICABLE;
    this._clientContact = ClientContactTypes.COMPANY;
  }
}
